import { useNavigate } from 'react-router-dom';
import { Plus, Globe, MessageCircle, TrendingUp, Trash2 } from 'lucide-react';

import { useTranslation } from '../../i18n';
import useModal from '../../hooks/useModal';
import useListParams from '../../hooks/useListParams';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import {
  useSpaces,
  useSpaceListStats,
  useDeleteSpace,
} from '../../hooks/queries/useSpaceQueries';
import { ISpaceWithStats } from '../../types';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import SpaceListView from './SpaceListView';
import SpaceFormDialog from './SpaceFormDialog';

const PAGE_SIZE = 20;

interface ISpaceManagementProps {
  initialSpaceId?: string;
  onNavigateDetail?: (id: string) => void;
  onBackToList?: () => void;
  onNavigateRoom?: (roomId: string) => void;
  onNavigateUser?: (userId: string) => void;
}

const SpaceManagement = ({
  onNavigateDetail,
  onNavigateUser,
}: ISpaceManagementProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { search, page } = useListParams();

  const formDialog = useModal<ISpaceWithStats>();
  const toggleConfirm = useConfirmDialog();

  const deleteSpace = useDeleteSpace();

  const { data: listStats } = useSpaceListStats();

  const { data, isLoading } = useSpaces({
    page,
    search,
    limit: PAGE_SIZE,
  });

  const spaces = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const handleViewDetail = (space: ISpaceWithStats) => {
    if (onNavigateDetail) {
      onNavigateDetail(space.id);
    } else {
      navigate(`/spaces/${space.id}`);
    }
  };

  const handleEdit = (space: ISpaceWithStats) => {
    formDialog.open(space);
  };

  const handleToggleStatus = (space: ISpaceWithStats) => {
    toggleConfirm.show(
      t('spaces.confirm_delete_space', { name: space.name }),
      async () => {
        await deleteSpace.mutateAsync({ id: space.id });
      },
    );
  };

  const handleNavigateUser = (userId: string) => {
    if (onNavigateUser) {
      onNavigateUser(userId);
    } else {
      navigate(`/users/${userId}`);
    }
  };

  const spaceStats = [
    {
      label: t('spaces.total_spaces'),
      value: (listStats?.totalSpaces ?? spaces.length).toString(),
      icon: <Globe className="w-8 h-8 text-blue-600" />,
      testId: 'SpaceManagement_List_totalSpacesText',
    },
    {
      label: t('spaces.room_count'),
      value: (listStats?.totalRooms ?? 0).toLocaleString(),
      icon: <MessageCircle className="w-8 h-8 text-purple-600" />,
      testId: 'SpaceManagement_List_roomCountText',
    },
    {
      label: t('spaces.today_messages'),
      value: (listStats?.todayMessages ?? 0).toLocaleString(),
      icon: <TrendingUp className="w-8 h-8 text-orange-600" />,
      testId: 'SpaceManagement_List_todayMessagesText',
    },
  ];

  return (
    <div className="p-4 lg:p-8 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('spaces.title')}
          </h1>
          <p className="text-base text-gray-600 mt-2">{t('spaces.subtitle')}</p>
        </div>
        <Button
          onClick={() => formDialog.open()}
          className="gap-2"
          data-testid="SpaceManagement_List_addSpaceButton"
        >
          <Plus className="w-4 h-4" />
          {t('spaces.add_space')}
        </Button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {spaceStats.map(({ label, value, icon, testId }) => (
          <Card key={label} className="py-6 px-6">
            <CardContent className="p-0">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-muted-foreground">
                    {label}
                  </div>
                  <div
                    className="text-3xl font-bold text-foreground"
                    data-testid={testId}
                  >
                    {value}
                  </div>
                </div>
                {icon}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <SpaceListView
        spaces={spaces}
        total={total}
        totalPages={totalPages}
        isLoading={isLoading}
        onViewDetail={handleViewDetail}
        onEdit={handleEdit}
        onViewMembers={handleViewDetail}
        onToggleStatus={handleToggleStatus}
        onNavigateUser={handleNavigateUser}
      />

      <SpaceFormDialog
        isOpen={formDialog.isOpen}
        onClose={formDialog.close}
        space={formDialog.selectedItem}
      />

      {/* Toggle Status Confirmation Dialog */}
      <Dialog
        open={toggleConfirm.isOpen}
        onOpenChange={(open) => !open && toggleConfirm.cancel()}
      >
        <DialogContent
          className="sm:max-w-[420px]"
          data-testid="SpaceManagement_List_deleteConfirmModal"
        >
          <DialogHeader>
            <DialogTitle>{t('spaces.toggle_status_title')}</DialogTitle>
            <DialogDescription>{toggleConfirm.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={toggleConfirm.cancel}
              data-testid="SpaceManagement_List_deleteConfirmModalCancelButton"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={toggleConfirm.confirm}
              disabled={toggleConfirm.isLoading}
              data-testid="SpaceManagement_List_deleteConfirmModalConfirmButton"
            >
              <Trash2 className="w-4 h-4" />
              {toggleConfirm.isLoading
                ? t('common.saving')
                : t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SpaceManagement;
